import { ErrorModal, useOpenModal } from '@modusign/design-system';
import { ErrorType } from '@modusign/openapi-client-user/custom-client';
import { useEffect } from 'react';
import { MutationOptions, useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { push, replace } from 'redux-first-history';

import { ThirdPartyIdProvider } from '@app/constants/IdProvider';
import useOpenPurgeDurationGuideModal from '@app/features/authentication/hooks/useOpenPurgeDurationGuideModal';
import { isSignInError } from '@app/features/authentication/hooks/useSignIn';
import lastLoginTypeService from '@app/libs/lastLoginTypeService';
import { login } from '@app/libs/login';
import { InactivePageState } from '@app/routes/Authentication/Inactive';
import { loadAccount } from '@app/store/account/actions';
import {
  thirdPartySignInFailure,
  thirdPartySignInSuccess,
} from '@app/store/account/third-party/actions';
import { modalOpen } from '@app/store/modal/actions';

export const getThirdPartySignInMutationKey = () => ['thirdPartySignIn'] as const;

export default function useThirdPartySignIn() {
  const client = useQueryClient();
  const dispatch = useDispatch();
  const openModal = useOpenModal();

  const openPurgeDurationGuideModal = useOpenPurgeDurationGuideModal();

  useEffect(() => {
    client.setMutationDefaults(getThirdPartySignInMutationKey(), {
      mutationFn: ({ code, provider }) => login({ type: 'oauth', code, provider }),
      onSuccess: (_, { redirectTo }) => {
        lastLoginTypeService.removeLastLoginType();
        [thirdPartySignInSuccess(), loadAccount()].forEach(dispatch);
        if (redirectTo) {
          dispatch(replace(redirectTo));
        }
      },
      onError: (error, { code, provider }) => {
        dispatch(thirdPartySignInFailure({ code, provider, message: error.message }));

        const { data: maybeErrorInstance } = error.response ?? {};
        switch (error.message) {
          case USER_BLOCKED:
            dispatch(modalOpen('ACCOUNT_SUSPENDED_MODAL'));
            break;
          case USER_IS_INACTIVATED:
            dispatch(
              push<InactivePageState>('/authentication/inactive', {
                code,
                provider,
              })
            );
            break;
          case INVALID_THIRD_PARTY_PROFILE:
            switch (provider) {
              case 'facebook':
                dispatch(
                  modalOpen('CONFIRM', {
                    title: '로그인 안내',
                    content: '해당 페이스북 계정은 모두싸인에\n가입하지 않은 계정입니다.',
                  })
                );
                break;
              case 'google':
                dispatch(
                  modalOpen('CONFIRM', {
                    title: '로그인 안내',
                    content:
                      '구글에서 모두싸인 로그인에 필요한 정보를\n제공하지 않아 로그인할 수 없습니다.',
                  })
                );
                break;
              case 'naver':
                dispatch(
                  modalOpen('NAVER_LOGIN_RE_AGREE_MODAL', {
                    isLogin: true,
                  })
                );
                break;
              default:
                openModal((props) => (
                  <ErrorModal
                    {...props}
                    error={error}
                    errorMessageMapper={THIRD_PARTY_SIGN_IN_ERRORS}
                    title="로그인 안내"
                  />
                ));
                break;
            }
            break;
          case THIRD_PARTY_USER_NOT_FOUND:
            switch (provider) {
              case 'facebook':
                dispatch(
                  modalOpen('CONFIRM', {
                    title: '로그인 안내',
                    content:
                      '지금 페이스북에 로그인한 계정이\n모두싸인 가입 계정이 아닙니다.\n확인 후, 다시 시도해 주세요.',
                  })
                );
                break;
              case 'google':
                dispatch(
                  modalOpen('CONFIRM', {
                    title: '로그인 안내',
                    content:
                      '지금 구글에 로그인한 계정이\n모두싸인 가입 계정이 아닙니다.\n확인 후, 다시 시도해 주세요.',
                  })
                );
                break;
              case 'naver':
                dispatch(
                  modalOpen('CONFIRM', {
                    title: '로그인 안내',
                    content:
                      '지금 네이버에 로그인한 계정이\n모두싸인 가입 계정이 아닙니다.\n확인 후, 다시 시도해 주세요.',
                  })
                );
                break;
              default:
                openModal((props) => (
                  <ErrorModal
                    {...props}
                    error={error}
                    errorMessageMapper={THIRD_PARTY_SIGN_IN_ERRORS}
                    title="로그인 안내"
                  />
                ));
                break;
            }
            break;
          default:
            if (isSignInError(maybeErrorInstance) && maybeErrorInstance.data.leavedAt) {
              openPurgeDurationGuideModal(maybeErrorInstance.data.leavedAt);
            } else {
              openModal((props) => (
                <ErrorModal
                  {...props}
                  error={error}
                  errorMessageMapper={THIRD_PARTY_SIGN_IN_ERRORS}
                  title="로그인 안내"
                />
              ));
            }
            break;
        }
      },
    } satisfies MutationOptions<
      void,
      ErrorType<Error>,
      { code: string; provider: ThirdPartyIdProvider; redirectTo?: string }
    >);
  }, [dispatch, openModal, openPurgeDurationGuideModal, client]);

  return useMutation<
    void,
    ErrorType<Error>,
    { code: string; provider: ThirdPartyIdProvider; redirectTo?: string }
  >({
    mutationKey: getThirdPartySignInMutationKey(),
  });
}

const INVALID_THIRD_PARTY_PROFILE = 'Invalid third party profile';
const THIRD_PARTY_USER_NOT_FOUND = 'Third party user not found';
const USER_IS_INACTIVATED = 'User is inactivated';
const USER_BLOCKED = 'User blocked';

const THIRD_PARTY_SIGN_IN_ERRORS: { [key: string]: string } = {
  [THIRD_PARTY_USER_NOT_FOUND]:
    '해당 소셜 계정으로 가입한 사용자가 아닙니다. 가입 여부 확인 후, 다시 시도해 주세요.',
  'not third party user':
    '해당 소셜 계정으로 가입한 사용자가 아닙니다. 이메일을 직접 입력하여 로그인하거나 가입 여부를 확인해 주세요.',
  'Left user': '이미 탈퇴한 사용자는 로그인할 수 없습니다.',
  'Different id provider': '다른 수단으로 가입한 사용자입니다. 가입한 수단으로 로그인해 주세요.',
};
